import { Button } from "primereact/button";
import { confirmPopup } from "primereact/confirmpopup";
import { DataView } from "primereact/dataview";
import { Panel } from "primereact/panel";
import { RadioButton } from "primereact/radiobutton";
import { Tag } from "primereact/tag";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useErrorHandler } from "src/core/error/useErrorHandler";
import { useDate } from "src/core/hooks/useDate";
import { useError } from "src/core/hooks/useError";
import { textService } from "src/core/services/textService";
import { AppointmentState } from "../../../../../../core/backend/clients/backendClient";
import { ToastContext } from "../../../../../../core/context/ToastContext";
import { logService } from "../../../../../../core/services/logService";
import type { AppointmentModel } from "../../../../../appointment/models/appointmentModel";
import { useAppointmentStore } from "../../../../../appointment/services/useAppointmentStore";
import { useAuthStore } from "../../../../../auth/services/useAuthStore";
import { ClinicModel } from "../../../../../clinics/models/clinicModel";
import { DoctorProfileModel } from "../../../../../clinics/models/doctorProfileModel";
import { useClinicsStore } from "../../../../../clinics/services/useClinicsStore";
import { ParticipantModel } from "../../../../../participant/models/participantModel";
import { useParticipantStore } from "../../../../../participant/services/useParticipantStore";
import "./HomeAppointments.css";

export function HomeAppointments() {
  const toastRef = useContext(ToastContext);

  const { t } = useTranslation();
  const navigate = useNavigate();
  const { dateFormat, timeFormat } = useDate();
  const { getErrorMessage } = useError();

  const authStore = useAuthStore();
  const clinicsStore = useClinicsStore();
  const participantStore = useParticipantStore();
  const appointmentStore = useAppointmentStore();
  const errorHandler = useErrorHandler();

  const appointmentFilterOptions = [
    {
      key: "appointmentSelectType1",
      label: t("Home.Appointments.SelectType1"),
      value: "active",
    },
    {
      key: "appointmentSelectType2",
      label: t("Home.Appointments.SelectType2"),
      value: "all",
    },
  ];

  const [appointmentFilterType, setAppointmentFilterType] = useState(
    appointmentFilterOptions[0]
  );
  const [filteredAppointments, setFilteredAppointments] =
    useState<AppointmentModel[]>();

  useEffect(() => {
    appointmentStore.appointmentList().catch((error) => {
      if (error.status === 401) {
        // Token expired or invalid
        authStore.logout();
        navigate("/");
      }
    });
  }, []);

  useEffect(() => {
    if (logService.globalLog)
      logService.log("appointments", appointmentStore.appointments);

    const clinicDoctors = clinicsStore.clinicDoctors;
    const participants = participantStore.participants;
    const appointments = appointmentStore.appointments;

    const clinicDoctorProfiles = clinicDoctors?.flatMap((clinicDoctor) =>
      clinicDoctor.profiles?.map((profile) => ({
        clinicDoctor: clinicDoctor,
        profile: profile,
      }))
    );

    const filteredAppointments = appointments
      .filter((appointment) => {
        return appointmentFilterType.value === "all"
          ? true
          : (appointment.dateTime ?? new Date()) > new Date() &&
              (appointment.state ===
                AppointmentState.WaitingForConfirmationConfirmationEmailNotSentYet ||
                appointment.state ===
                  AppointmentState.WaitingForConfirmationConfirmationEmailSent ||
                appointment.state ===
                  AppointmentState.ConfirmedNotSychronized ||
                appointment.state === AppointmentState.ConfirmedAndSychronized);
      })
      .map((appointment) => {
        let participantName = "";
        if (participants.length > 0) {
          const participant = participants.find(
            (participant) => participant.id === appointment.participantId
          );
          if (participant) participantName += participant.name ?? "";
          else participantName += authStore.user.name;
        }

        const clinicDoctorProfile = clinicDoctorProfiles.find(
          (clinicDoctorProfile) =>
            clinicDoctorProfile.profile?.id === appointment.profileId
        );

        if (clinicDoctorProfile) {
          appointment.participantName = participantName;
          appointment.clinicId = clinicDoctorProfile.clinicDoctor.clinicId;
          appointment.clinicName = clinicDoctorProfile.clinicDoctor.clinicName;
          appointment.clinicAddressFormatted =
            clinicDoctorProfile.clinicDoctor.clinicAddress;
          appointment.clinicProfessionName =
            clinicDoctorProfile.clinicDoctor.clinicProfessionName;
          appointment.doctorId = clinicDoctorProfile.clinicDoctor.doctorId;
          appointment.doctorName = clinicDoctorProfile.clinicDoctor.doctorName;

          const profile = clinicDoctorProfile.clinicDoctor.profiles.find(
            (p) => p.id === appointment.profileId
          );

          appointment.profileName =
            profile?.name === "Általános" ? "" : profile?.name ?? "";

          // severity: 'success' | 'info' | 'warning' | 'danger' | 'help' | 'primary' | 'secondary' | 'contrast' | null | undefined;
          appointment.severity =
            appointment.state === AppointmentState.ConfirmedNotSychronized ||
            appointment.state === AppointmentState.ConfirmedAndSychronized
              ? "success"
              : appointment.state ===
                  AppointmentState.WaitingForConfirmationConfirmationEmailNotSentYet ||
                appointment.state ===
                  AppointmentState.WaitingForConfirmationConfirmationEmailSent
              ? "warning"
              : "danger";

          appointment.isDeletable =
            appointment.state !== AppointmentState.DeletedByDoctor &&
            appointment.state !== AppointmentState.DeletedByDoctorInHIS &&
            appointment.state !== AppointmentState.DeletedByUser;
        }

        return appointment;
      })
      .sort((a, b) => {
        return (
          (b.dateTime ?? new Date()).getTime() -
          (a.dateTime ?? new Date()).getTime()
        );
      });

    if (logService.globalLog)
      logService.log("filteredAppointments", filteredAppointments);

    setFilteredAppointments(filteredAppointments);
  }, [
    clinicsStore.clinicDoctors,
    participantStore.participants,
    appointmentStore.appointments,
    appointmentFilterType,
  ]);

  const onAppointmentDelete = (
    event: React.MouseEvent<HTMLButtonElement>,
    appointment: AppointmentModel
  ) => {
    logService.log("onAppointmentDelete", appointment);

    if (!appointment.id || appointment.id === 0) return;

    confirmPopup({
      target: event.target as HTMLElement,
      message: t("Home.Appointments.DeleteSure"),
      icon: "pi pi-question-circle",
      defaultFocus: "accept",
      accept: async () => {
        appointment.isLoading = true;

        await appointmentStore
          .appointmentDelete(appointment.id ?? 0)
          .then(() => {
            toastRef?.current?.show({
              severity: "success",
              summary: t("Home.Appointments.Title"),
              detail: t("Home.Appointments.DeleteSuccess"),
              life: 5000,
            });

            appointmentStore.appointmentList();
          })
          .catch((error) => {
            errorHandler.setError({
              title: t("Home.Appointments.Title"),
              message:
                t("Home.Appointments.DeleteFailed") +
                " " +
                getErrorMessage(error),
            });
          });

        appointment.isLoading = false;
      },
    });
  };

  const onAppointmentBookAgain = (appointment: AppointmentModel) => {
    if (logService.globalLog) logService.log("onAppointmentAgain", appointment);

    appointmentStore.setSelectedPerson(
      new ParticipantModel({
        id: appointment.participantId ?? 0,
        userId: appointment.userId ?? 0,
        name:
          (appointment.participantName?.length ?? 0) > 0
            ? appointment.participantName
            : t("Home.Appointments.ForMyself"),
        phone: authStore.user?.phone,
      })
    );

    appointmentStore.setSelectedClinic(
      new ClinicModel({
        id: appointment.clinicId,
        name: appointment.clinicName,
      })
    );

    appointmentStore.setSelectedDoctorProfile(
      new DoctorProfileModel({
        doctorId: appointment.doctorId,
        doctorName: appointment.doctorName,
        profileId: appointment.profileId,
        profileName: appointment.profileName,
      })
    );

    appointmentStore.setQuickAppointment(true);

    navigate("/appointment");
  };

  return (
    <div className="homeAppointments">
      <Panel
        header={
          <div className="flex align-items-center gap-2">
            <span className="pi pi-calendar-clock"></span>
            <span className="font-bold break-word">
              {t("Home.Appointments.Current")}
            </span>
          </div>
        }
        icons={
          <Button
            icon="pi pi-calendar-plus"
            label={t("Home.Appointments.New")}
            text
            onClick={() => {
              navigate("/appointment");
            }}
          ></Button>
        }
      >
        <>
          <div className="flex flex-column sm:flex-row justify-content-center gap-1 sm:gap-3">
            {appointmentFilterOptions.map((option) => {
              return (
                <div
                  key={option.key}
                  className="flex align-items-center"
                >
                  <RadioButton
                    inputId={option.key}
                    name="category"
                    value={option}
                    onChange={(e) => setAppointmentFilterType(e.value)}
                    checked={appointmentFilterType.key === option.key}
                  />
                  <label
                    htmlFor={option.key}
                    className="ml-2"
                  >
                    {option.label}
                  </label>
                </div>
              );
            })}
          </div>
          <hr />

          <DataView
            value={filteredAppointments}
            listTemplate={(items?: AppointmentModel[]) => {
              if (!items || items.length === 0) return null;

              let list = items.map((item, index) => {
                return (
                  <div
                    key={item.id}
                    className={
                      "col-12 flex flex-column sm:flex-row sm:align-items-center gap-3 " +
                      (index > 0 ? "border-top-1 surface-border" : "")
                    }
                  >
                    <Tag severity={item.severity}>
                      <div className="flex flex-column align-items-center font-bold text-lg">
                        <div>
                          {textService.formatDate(item.dateTime, dateFormat)}
                        </div>
                        <div>
                          {textService.formatDate(item.dateTime, timeFormat)}
                        </div>
                      </div>
                    </Tag>

                    <div className="flex flex-row flex-grow-1 align-items-center">
                      <div className="flex flex-column flex-grow-1">
                        <div className="font-bold font-italic">
                          {item.participantName}
                        </div>
                        <div className="font-bold">{item.doctorName}</div>
                        <div>{item.clinicName}</div>
                        <div>{item.clinicAddressFormatted}</div>
                        {(item.notes?.length ?? 0) > 0 && (
                          <div className="text-left">
                            <span className="font-italic">
                              {t("Home.Appointments.Notes")}
                            </span>
                            :<span className="font-bold">{item.notes}</span>
                          </div>
                        )}
                        <div>
                          <Button
                            icon="pi pi-replay"
                            label={t("Home.Appointments.Again")}
                            severity="contrast"
                            onClick={() => onAppointmentBookAgain(item)}
                          ></Button>
                        </div>
                      </div>

                      <div>
                        {item.isDeletable && (
                          <Button
                            icon="pi pi-trash"
                            severity="danger"
                            onClick={(event) =>
                              onAppointmentDelete(event, item)
                            }
                          ></Button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              });

              return <>{list}</>;
            }}
          />
        </>
      </Panel>
    </div>
  );
}
